import { Box, Button, Collapse, IconButton, Theme, Typography } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { format } from 'date-fns';
import { useContext, useState } from 'react';
import { User } from '../../../../types';
import useUserApiRoutes from '../../../hooks/api/useUserApiRoutes';
import SnackBarContext from '../../../context/snackbar';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      borderRadius: '24px',
      boxShadow: '0px 2px 16px 1px rgba(0, 0, 0, 0.12)',
      marginBottom: theme.spacing(3),
    },
    count: {
      color: theme.palette.error.main,
    },
    countClear: {
      color: theme.palette.info.main,
    },
    row: {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      borderBottom: `1px solid ${theme.palette.grey[100]}`,
    },
    header: {
      cursor: 'pointer',
      // Cover the card's padding so the whole collapsed card is clickable
      margin: theme.spacing(-3),
      padding: theme.spacing(3),
    },
    chevron: {
      transition: 'transform 0.2s',
    },
    chevronExpanded: {
      transform: 'rotate(180deg)',
    },
  })
);

// New signups awaiting admin approval. Users sit locked out of the app (status
// 'pending-approval') until they are approved or rejected here. Rejecting
// freezes the account, which blocks access and emails the user.
const PendingUsersCard = ({ pendingUsers, onResolved }: { pendingUsers: User[]; onResolved: () => void }) => {
  const classes = useStyles();
  const { updateUserStatus } = useUserApiRoutes();
  const { showSnack } = useContext(SnackBarContext);
  const [busyUserId, setBusyUserId] = useState<string>();
  // Collapsed by default so the card is just a one-line summary until the
  // admin wants to action the signups.
  const [expanded, setExpanded] = useState(false);

  // Oldest first: the longest-waiting signups are the most urgent.
  const sorted = [...pendingUsers].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  const resolveUser = (user: User, status: 'activate' | 'frozen') => {
    const name = user.fullName || user.email;
    if (
      status === 'frozen' &&
      !window.confirm(`Reject ${name}? Their account will be frozen and they will be notified by email.`)
    ) {
      return;
    }
    setBusyUserId(user._id);
    updateUserStatus(status, user._id)
      .then(() => {
        showSnack(status === 'activate' ? `${name} has been approved` : `${name} has been rejected`, 'success');
        onResolved();
      })
      .catch(() => showSnack(`Could not update ${name}`, 'error'))
      .finally(() => setBusyUserId(undefined));
  };

  return (
    <Box className={classes.container}>
      <Box
        className={classes.header}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="h6" fontWeight={600}>
          Pending Users
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h4" fontWeight={800} className={sorted.length ? classes.count : classes.countClear}>
            {sorted.length}
          </Typography>
          <IconButton size="small">
            <ExpandMoreIcon className={`${classes.chevron} ${expanded ? classes.chevronExpanded : ''}`} />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expanded}>
        {sorted.length === 0 && (
          <Typography variant="body2" color="textSecondary" mt={1}>
            No signups waiting on approval
          </Typography>
        )}
        <Box mt={1}>
          {sorted.map((user) => (
            <Box key={user._id} className={classes.row} display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {user.fullName || user.email}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {user.email}
                  {user.companyInformation?.companyName ? ` · ${user.companyInformation.companyName}` : ''}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="caption" color="textSecondary">
                  {format(new Date(user.created_at), 'd MMM')}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={busyUserId === user._id}
                  onClick={() => resolveUser(user, 'activate')}
                >
                  Approve
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  disabled={busyUserId === user._id}
                  onClick={() => resolveUser(user, 'frozen')}
                >
                  Reject
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default PendingUsersCard;
